/**
 * Garde d'accès de la SPA (ARCHITECTURE.md §5.1, specs §9.2).
 *
 * Un seul appel décide de tout : `/api/auth/status`. Trois issues, dans cet
 * ordre — compte à créer, session à ouvrir, application.
 *
 * Ce n'est pas une protection : la seule barrière qui compte est le hook
 * `preHandler` côté serveur, qui refuse toute route non publique sans session.
 * Ici on ne fait qu'éviter d'afficher une coquille vide qui enchaînerait les 401.
 *
 * 🔴 **C'est cette garde qui décide si l'application s'ouvre hors ligne**
 * (`ARCHITECTURE_V2.md` §3.1, étape 2 de la V2). Le point est facile à manquer :
 * on peut persister parfaitement le journal, les modèles et les pesées — si la
 * garde affiche « API injoignable » sans réseau, rien de tout cela n'est
 * jamais atteint. Elle interroge `/api/auth/status` à **chaque montage**
 * (`staleTime: 0`), et cet appel échoue par construction sans réseau.
 *
 * D'où les deux moitiés du correctif, l'une sans l'autre ne servant à rien :
 *
 *   - `lib/persist.ts` inscrit `['auth', 'status']` dans les clés persistées ;
 *   - la garde **préfère la réponse en cache à un échec de transport**, au lieu
 *     de traiter les deux natures d'échec de la même façon.
 */

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { fetchAuthStatus } from '../../api/auth';
import { isTransportFailure } from '../../api/client';
import { BootstrapScreen } from './BootstrapScreen';
import { LoginScreen } from './LoginScreen';
import { Button, ErrorNote, Hint } from '../../components/ui/primitives';
import { AuthShell } from './parts';

export function AuthGate({ children }: { children: ReactNode }) {
  const status = useQuery({
    queryKey: ['auth', 'status'],
    queryFn: fetchAuthStatus,
    // L'état d'authentification ne se devine pas : il est relu à chaque montage
    // et jamais servi périmé.
    staleTime: 0,
    retry: false,
  });

  if (status.isPending) {
    return (
      <AuthShell title="Nutri">
        <Hint>Ouverture…</Hint>
      </AuthShell>
    );
  }

  /*
   * 🔴 **Un échec de transport ne referme pas l'application si l'on sait déjà
   * qui est connecté.**
   *
   * ⚠️ Piège de TanStack Query, et c'est lui qui rendait la garde bloquante :
   * une requête qui possède déjà des données **passe quand même en `error`**
   * quand son rafraîchissement échoue. `status.data` est alors parfaitement
   * renseigné, mais `isError` vaut `true` — tester l'erreur avant les données,
   * ce que faisait le code d'origine, revient donc à jeter un cache utilisable.
   *
   * Le critère est celui de l'étape 1 : « le serveur a-t-il répondu ? ». Un
   * échec de **transport** avec des données en cache est le cas nominal du hors
   * ligne, et le §3.5 est catégorique — ne jamais le présenter comme une
   * erreur. Un refus **applicatif** (le serveur a répondu, et il refuse) reste
   * affiché : c'est une vraie panne, et elle ne se répare pas en attendant.
   *
   * ⚠️ **Ce n'est pas un contournement d'authentification.** Rien ici n'est une
   * barrière — le hook `preHandler` du serveur refuse toute route non publique
   * sans session, et il ne voit jamais ce cache. Ouvrir sur des données locales
   * ne donne accès à rien de plus que ce que l'appareil détenait déjà. Si la
   * session s'est éteinte entre-temps, le premier appel au retour du réseau
   * répond 401, `main.tsx` invalide `['auth', 'status']`, et la garde bascule.
   */
  const horsLigne = status.isError && status.data !== undefined && isTransportFailure(status.error);

  if (status.isError && !horsLigne) {
    /*
     * ⚠️ **Deux situations, deux phrases** (§3.5 : « ne jamais présenter hors
     * ligne comme une erreur »). Sans réseau **et** sans rien en cache, il n'y a
     * effectivement rien à afficher — mais ce n'est pas une panne de
     * l'application, et l'annoncer comme telle enverrait chercher un défaut là
     * où il n'y en a pas. Le seul cas qui mérite le mot « injoignable » est
     * celui où le serveur a répondu, et mal.
     *
     * Le bouton reste le même dans les deux cas : c'est la seule chose utile à
     * faire, et elle est vraie des deux côtés.
     */
    const transport = isTransportFailure(status.error);
    return (
      <AuthShell title="Nutri">
        {transport ? (
          <Hint>
            Pas de connexion, et rien en mémoire sur cet appareil. Se connecter une première
            fois au réseau rendra ensuite l’application consultable hors ligne.
          </Hint>
        ) : (
          <ErrorNote>API injoignable : {status.error.message}</ErrorNote>
        )}
        <Button
          onClick={() => {
            void status.refetch();
          }}
        >
          Réessayer
        </Button>
      </AuthShell>
    );
  }

  /*
   * ⚠️ Sans réseau, on ne peut ni amorcer un compte ni ouvrir une session : les
   * deux sont des cérémonies WebAuthn, non rejouables par nature (§3.1). Ces
   * deux écrans restent donc affichés tels quels — ils expliqueront l'échec au
   * moment du geste, ce qui est plus juste qu'un bandeau posé d'avance.
   */
  if (status.data === undefined) return null;
  if (status.data.bootstrapRequired) return <BootstrapScreen />;
  if (!status.data.authenticated) return <LoginScreen />;

  return <>{children}</>;
}
